import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { onErrorAlert } from 'common/helper';
import { BehaviorSubject, Observable, Subject, Subscription, catchError, finalize, map, of, tap } from 'rxjs';

import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { FormStore } from '../common/form.store';
import { EEvent, TPostMessage } from '../core/Events';
import { ParentWindowService } from '../core/parent-window.service';
import { AttendanceFormService } from '../services/attendance-form.service';
import { AttendanceContactDetail, AttendanceInfo, IAttendanceContactAnswerHttp, IAttendanceFormVariant, IAttendanceSaveRequestHttp } from 'attendance/model/attendance.model';
import { DetailInfo } from 'common/model';
import { FormBuilder } from '@angular/forms';
import { assignDetailInfoValue, compareValidationResult, prepareDetailDataRequest } from 'shared/shared.helper';
import { IDetailInfoDataHttp } from 'common/model/common.model';
import { AttendanceContactPageData } from 'shared/contact-list/contact-list.component';

@Component({
  templateUrl: './attendance-form.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class AttendanceFormComponent implements OnInit {
  @ViewChild('container') container: ElementRef;

  @Input() set baseApiUrl(apiUrl: string | undefined) {
    if (apiUrl) {
      environment.baseUrl = apiUrl;
    }
  }

  @Input() set baseFormController(controller: string | undefined) {
    if (controller) {
      environment.clientFormController = controller;
    }
  }

  @Input() token: string | undefined;
  @Input() preview = false;

  public attendanceDataObs!: Observable<AttendanceInfo>;

  public detailsInfos: DetailInfo[] = [];
  public evideInputContentFormGuid = '';
  public attendanceContactDetail: AttendanceContactDetail;

  public captchaSiteKeyObs!: Observable<string>;
  public recaptcha: string;

  private clientToken?: string | null = null;
  private formGuid?: string | null = null;
  public isValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public pageData: AttendanceContactPageData = {
    formGroup: this.fb.group({}),
    gridRowLists: [],
    formGuid: '',
    activitySessionGuid: '',
    isUnsaved: false,
    formSaveSuccess$: new Subject<boolean>()
  };

  public processing$ = new BehaviorSubject<boolean>(false);
  public submitted = false;

  public formLevelVariants: IAttendanceFormVariant[] = [];
  public formLevelSelectedVariantGuid: string | null = null;

  private subs = new Subscription();

  constructor(
    route: ActivatedRoute,
    private fb: FormBuilder,
    private router: Router,
    private alert: ToastrService,
    private parentWindow: ParentWindowService,
    private attendanceFormService: AttendanceFormService,
  ) {
    this.clientToken = route.snapshot.queryParamMap.get('formurl'); // For client
    this.formGuid = route.snapshot.paramMap.get('guid'); // For preview

    const sub = FormStore.resize$.subscribe(this.onResize.bind(this));
    this.subs.add(sub);
  }

  @HostListener('window:beforeunload', ['$event'])
  checkReload(event: any) {
    event.preventDefault();
    if (this.pageData.isUnsaved) {
      event.returnValue =
        'You have unsaved changes! Are you sure you want to leave?';
    }
  }

  ngOnInit() {
    this.init();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private init() {
    if (this.clientToken) {
      FormStore.token = this.clientToken;
      this.getFormDataFromUrl();
      this.getRecaptchaSiteKey();
    } else if (this.formGuid && this.token) {
      FormStore.token = this.token;
      this.getFormDataFromGuid(this.formGuid);
    } else {
      this.isValid$.next(false);
    }
    this.fetchContactDetail();
  }

  private getFormDataFromUrl() {
    this.attendanceDataObs = this.attendanceFormService.getAttendanceFormInfo().pipe(
      tap(res => this.prepareClientInputContents(res)),
      catchError(this.onGetError.bind(this)),
      finalize(() => {
        FormStore.resize$.next();
      })
    );

  }

  private getFormDataFromGuid(guid: string) {
    this.attendanceDataObs = this.attendanceFormService.getAttendanceFormInfo().pipe( // guid
      tap(res => this.prepareClientInputContents(res)),
      catchError(this.onGetError.bind(this)),
    );
  }

  private fetchContactDetail() {
    this.attendanceFormService.getAttendanceContactGridInfo().subscribe({
      next: (result: AttendanceContactDetail) => {
        this.attendanceContactDetail = result;
      },
      error: err => onErrorAlert(this.alert, err)
    });
  }

  private prepareClientInputContents(form: AttendanceInfo) {
    this.detailsInfos = [];
    form.groupInfos.forEach(group => group.detailInfos.forEach(inputContent => {
      group.detailInfos.forEach(detailInfo => {
        assignDetailInfoValue(detailInfo, form.programDetailValue);
      });
      this.detailsInfos.push(inputContent);
    }))
    this.evideInputContentFormGuid = form.evideInputContentFormGuid;
    this.formLevelVariants = form.variants ?? [];
    this.formLevelSelectedVariantGuid = form.selectedVariantGuid ?? null;
  }

  public onFormLevelVariantChange(variantGuid: string | null): void {
    this.formLevelSelectedVariantGuid = variantGuid;
  }

  private getRecaptchaSiteKey() {
    this.captchaSiteKeyObs = this.attendanceFormService.getCaptchaInfo().pipe(
      map(res => res.dataSiteKey),
      catchError(this.onGetError.bind(this))
    );
  }

  public recaptchaResolved(val: string) {
    this.recaptcha = val;
  }

  private onGetError() {
    this.isValid$.next(false);
    return of();
  }

  public onFormSubmit(attendanceForm) {
    this.submitted = true;
    const contactListForm = this.pageData.formGroup;
    contactListForm.markAllAsTouched();
    FormStore.touched$.next();
    if (contactListForm.invalid) {
      return;
    }

    const rawValue = contactListForm.getRawValue();

    const contactSaveHttpRequests: IAttendanceContactAnswerHttp[] = this.pageData.gridRowLists.map(contactRow => {
      const contactDetailInfo: DetailInfo[] = [];
      const detailDataHttp: Array<IDetailInfoDataHttp> = [];
      contactRow.attendanceContacts.forEach(programContact => {
        contactDetailInfo.push(programContact.detailInfo);
      });
      detailDataHttp.push(...prepareDetailDataRequest({ [contactRow.uuid]: rawValue[contactRow.uuid] }, contactDetailInfo))
      return {
        contactGuid: contactRow.contactGuid,
        detailData: detailDataHttp,
        evideInputContentFormGuid: this.pageData.formGuid
      };
    });


    if (attendanceForm) {
      this.save(attendanceForm, contactSaveHttpRequests);
    }
  }

  private save(formObj: any, contactAnswers: Array<IAttendanceContactAnswerHttp>) {
    const body = this.prepareBody(formObj, contactAnswers);
    if (!body || !this.clientToken) { return; }
    this.processing$.next(true);

    this.attendanceFormService.saveAttendanceFormAnswer(body).pipe(
      finalize(() => this.processing$.next(false))
    ).subscribe({
      next: (res) => {
        FormStore.reset$.next();
        this.pageData.isUnsaved = false;
        this.pageData.formSaveSuccess$.next(true);
        const state = { successMessage: res.message };
        this.router.navigateByUrl('/success', { state });
      },
      error: (err) => onErrorAlert(this.alert, err)
    });
  }

  private prepareBody(formObj: any, contactAnswers: Array<IAttendanceContactAnswerHttp>): IAttendanceSaveRequestHttp | null {

    if (!this.clientToken) { return null; }

    if (!this.recaptcha) {
      this.alert.error('Please validate captcha!');
      return
    }

    const detailData: IDetailInfoDataHttp[] = prepareDetailDataRequest(formObj, this.detailsInfos);
    const request: IAttendanceSaveRequestHttp = {
      // attendanceGuid: Guid.createEmpty().toString(),
      evideInputContentFormGuid: this.evideInputContentFormGuid,
      detailData,
      attendanceContactAnswerInfos: contactAnswers,
      encodedResponse: this.recaptcha,
      selectedVariantGuid: this.formLevelSelectedVariantGuid
    };
    var validationMessage = compareValidationResult(detailData, this.detailsInfos);
    if (!validationMessage.isValid) {
      this.alert.error(validationMessage.errorMessage);
      return
    }
    return request;
  }

  private onResize() {
    setTimeout(() => {
      this.resizeContainerHeight();
    }, 100);
  }

  // Publish message to iframe saying that it's height changed
  private resizeContainerHeight() {
    const message: TPostMessage = {
      name: EEvent.EvideFormHeightChange,
      data: {
        height: this.container?.nativeElement.scrollHeight
      }
    };
    this.parentWindow.postMessage(message);
  }

}
